import { ReactElement, ReactNode, useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useMediaQuery } from '@mui/material';

import { CourseTee } from '../../types/Course';

interface CourseTeeViewProps {
  tee: CourseTee;
  isAdmin?: boolean;
  onEdit?: () => void;
  actionSlot?: ReactNode;
}

const cellSx = {
  px: '0.25rem',
  py: '0.125rem',
  fontSize: '0.8rem',
  width: '2em',
  minWidth: '2em',
  textAlign: 'center' as const,
  borderRight: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap' as const,
};

const labelSx = {
  ...cellSx,
  width: '3.5em',
  minWidth: '3.5em',
  fontWeight: 'bold',
  textAlign: 'left' as const,
};

const subtotalSx = {
  ...cellSx,
  width: '3.5em',
  minWidth: '3.5em',
  fontWeight: 'bold',
  backgroundColor: 'action.hover',
};

const totalSx = {
  ...subtotalSx,
  width: '4em',
  minWidth: '4em',
};

function HoleTable({ tee, start, end, subtotalLabel, showTotal }: {
  tee: CourseTee;
  start: number;
  end: number;
  subtotalLabel?: string;
  showTotal?: boolean;
}) {
  const slice = (arr: number[] | undefined) => arr?.slice(start, end) ?? [];
  const rangeSum = (arr: number[] | undefined) =>
    arr?.slice(start, end).reduce((s, v) => s + v, 0) ?? 0;
  const grandTotal = (arr: number[] | undefined) =>
    arr?.reduce((s, v) => s + v, 0) ?? 0;

  const hasMensHcp = tee.isForMen !== false && (tee.mensStrokeIndex?.some(v => v > 0) ?? false);
  const hasWomensHcp = tee.isForWomen !== false && (tee.womensStrokeIndex?.some(v => v > 0) ?? false);

  return (
    <Table size="small" sx={{ tableLayout: 'auto', width: 'auto' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={labelSx}>Hole</TableCell>
          {Array.from({ length: end - start }, (_, i) => (
            <TableCell key={i} sx={cellSx}>{start + i + 1}</TableCell>
          ))}
          {subtotalLabel && <TableCell sx={subtotalSx}>{subtotalLabel}</TableCell>}
          {showTotal && <TableCell sx={totalSx}>Tot</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell sx={labelSx}>Yds</TableCell>
          {slice(tee.yardage).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
          {subtotalLabel && <TableCell sx={subtotalSx}>{rangeSum(tee.yardage).toLocaleString()}</TableCell>}
          {showTotal && <TableCell sx={totalSx}>{grandTotal(tee.yardage).toLocaleString()}</TableCell>}
        </TableRow>
        <TableRow>
          <TableCell sx={labelSx}>Par</TableCell>
          {slice(tee.par).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
          {subtotalLabel && <TableCell sx={subtotalSx}>{rangeSum(tee.par)}</TableCell>}
          {showTotal && <TableCell sx={totalSx}>{grandTotal(tee.par)}</TableCell>}
        </TableRow>
        {hasMensHcp && (
          <TableRow>
            <TableCell sx={labelSx}>M Hcp</TableCell>
            {slice(tee.mensStrokeIndex).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
            {subtotalLabel && <TableCell sx={subtotalSx} />}
            {showTotal && <TableCell sx={totalSx} />}
          </TableRow>
        )}
        {hasWomensHcp && (
          <TableRow>
            <TableCell sx={labelSx}>W Hcp</TableCell>
            {slice(tee.womensStrokeIndex).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
            {subtotalLabel && <TableCell sx={subtotalSx} />}
            {showTotal && <TableCell sx={totalSx} />}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function CombinedHoleTable({ tee }: { tee: CourseTee }) {
  const slice = (arr: number[] | undefined, s: number, e: number) => arr?.slice(s, e) ?? [];
  const rangeSum = (arr: number[] | undefined, s: number, e: number) =>
    arr?.slice(s, e).reduce((sum, v) => sum + v, 0) ?? 0;
  const total = (arr: number[] | undefined) => arr?.reduce((sum, v) => sum + v, 0) ?? 0;

  const hasMensHcp = tee.isForMen !== false && (tee.mensStrokeIndex?.some(v => v > 0) ?? false);
  const hasWomensHcp = tee.isForWomen !== false && (tee.womensStrokeIndex?.some(v => v > 0) ?? false);

  return (
    <Table size="small" sx={{ tableLayout: 'auto', width: 'auto' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={labelSx}>Hole</TableCell>
          {Array.from({ length: 9 }, (_, i) => <TableCell key={i} sx={cellSx}>{i + 1}</TableCell>)}
          <TableCell sx={subtotalSx}>Out</TableCell>
          {Array.from({ length: 9 }, (_, i) => <TableCell key={i + 9} sx={cellSx}>{i + 10}</TableCell>)}
          <TableCell sx={subtotalSx}>In</TableCell>
          <TableCell sx={totalSx}>Tot</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell sx={labelSx}>Yds</TableCell>
          {slice(tee.yardage, 0, 9).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
          <TableCell sx={subtotalSx}>{rangeSum(tee.yardage, 0, 9).toLocaleString()}</TableCell>
          {slice(tee.yardage, 9, 18).map((v, i) => <TableCell key={i + 9} sx={cellSx}>{v}</TableCell>)}
          <TableCell sx={subtotalSx}>{rangeSum(tee.yardage, 9, 18).toLocaleString()}</TableCell>
          <TableCell sx={totalSx}>{total(tee.yardage).toLocaleString()}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={labelSx}>Par</TableCell>
          {slice(tee.par, 0, 9).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
          <TableCell sx={subtotalSx}>{rangeSum(tee.par, 0, 9)}</TableCell>
          {slice(tee.par, 9, 18).map((v, i) => <TableCell key={i + 9} sx={cellSx}>{v}</TableCell>)}
          <TableCell sx={subtotalSx}>{rangeSum(tee.par, 9, 18)}</TableCell>
          <TableCell sx={totalSx}>{total(tee.par)}</TableCell>
        </TableRow>
        {hasMensHcp && (
          <TableRow>
            <TableCell sx={labelSx}>M Hcp</TableCell>
            {slice(tee.mensStrokeIndex, 0, 9).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
            <TableCell sx={subtotalSx} />
            {slice(tee.mensStrokeIndex, 9, 18).map((v, i) => <TableCell key={i + 9} sx={cellSx}>{v}</TableCell>)}
            <TableCell sx={subtotalSx} />
            <TableCell sx={totalSx} />
          </TableRow>
        )}
        {hasWomensHcp && (
          <TableRow>
            <TableCell sx={labelSx}>W Hcp</TableCell>
            {slice(tee.womensStrokeIndex, 0, 9).map((v, i) => <TableCell key={i} sx={cellSx}>{v}</TableCell>)}
            <TableCell sx={subtotalSx} />
            {slice(tee.womensStrokeIndex, 9, 18).map((v, i) => <TableCell key={i + 9} sx={cellSx}>{v}</TableCell>)}
            <TableCell sx={subtotalSx} />
            <TableCell sx={totalSx} />
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export default function CourseTeeView({ tee, isAdmin, onEdit, actionSlot }: CourseTeeViewProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const isMd = useMediaQuery((t: Theme) => t.breakpoints.up('md'));

  const totalYardage = tee.yardage?.reduce((sum, y) => sum + y, 0) ?? 0;
  const totalPar = tee.par?.reduce((sum, p) => sum + p, 0) ?? 0;
  const holeCount = tee.yardage?.length ?? 0;
  const is18 = holeCount === 18;
  const hasHoles = holeCount > 0;
  const stacked = !isMd && is18;

  const yardageParText = [
    totalYardage > 0 ? `${totalYardage.toLocaleString()} yds` : null,
    totalPar > 0 ? `Par ${totalPar}` : null,
  ].filter(Boolean).join(' · ');

  const ratingSlope = (
    <Stack direction="column" sx={{ lineHeight: 1.2, flexShrink: 0 }}>
      {tee.isForMen !== false && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{`M: ${tee.mensRating || '—'}/${tee.mensSlope || '—'}`}</Typography>
      )}
      {tee.isForWomen !== false && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{`W: ${tee.womensRating || '—'}/${tee.womensSlope || '—'}`}</Typography>
      )}
    </Stack>
  );

  const expandIcon = hasHoles ? (
    <Box sx={{ flexShrink: 0, color: 'text.secondary' }}>
      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </Box>
  ) : null;

  const trailingSlots = (
    <>
      {actionSlot && (
        <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          {actionSlot}
        </Box>
      )}
      {isAdmin && (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit?.(); }} aria-label={`Edit ${tee.name || 'tee'}`} sx={{ flexShrink: 0 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );

  return (
    <Box sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: { xs: 0, md: 1 },
      px: 1.5,
      py: 1,
      width: '100%',
      ...(isMd ? { maxWidth: '55rem', mx: 'auto' } : {}),
    }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', cursor: hasHoles ? 'pointer' : 'default' }}
        onClick={() => hasHoles && setExpanded((v) => !v)}
      >
        <Box sx={{ width: '1rem', height: '1rem', borderRadius: '50%', bgcolor: tee.color || '#cccccc', border: '1px solid', borderColor: 'divider', flexShrink: 0 }} />
        {tee.combo && tee.color2 && (
          <Box sx={{ width: '1rem', height: '1rem', borderRadius: '50%', bgcolor: tee.color2, border: '1px solid', borderColor: 'divider', flexShrink: 0 }} />
        )}

        {isMd ? (
          // Large screen: single row — name, yardage/par, rating, icons
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tee.name || '(unnamed)'}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexShrink: 0 }}>
              {yardageParText && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{yardageParText}</Typography>
              )}
              {ratingSlope}
            </Stack>
          </>
        ) : (
          // Small screen: name + yardage/par stacked, rating to the right
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tee.name || '(unnamed)'}
            </Typography>
            {yardageParText && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{yardageParText}</Typography>
            )}
          </Stack>
        )}

        {!isMd && ratingSlope}
        {expandIcon}
        {trailingSlots}
      </Stack>

      {hasHoles && (
        <Collapse in={expanded}>
          <Box sx={{ overflowX: 'auto', mt: 1 }}>
            {is18 && !stacked ? (
              <CombinedHoleTable tee={tee} />
            ) : is18 ? (
              <Stack spacing={1}>
                <HoleTable tee={tee} start={0} end={9} subtotalLabel="Out" />
                <HoleTable tee={tee} start={9} end={18} subtotalLabel="In" showTotal />
              </Stack>
            ) : (
              <HoleTable tee={tee} start={0} end={holeCount} showTotal />
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
